import { Fragment, useState } from 'react';
import { Await, Link, useLoaderData } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';

import { AdPlacementRail } from '@/components/site/ad-slot';
import { HomeAlertsStrip } from '@/components/site/home-alerts-strip';
import { HomeSidebar } from '@/components/site/home-sidebar';
import { HomeShabbatStrip } from '@/components/site/shabbat-strip';
import { ObfuscatedContactEmail } from '@/components/site/obfuscated-contact-email';
import {
    CategoryRail,
    FeaturedHero,
    PictureMoment,
    WeeklyTopRanking,
    SectionArticles,
    ArticleRow,
    type CategoryRailVariant,
} from '@/components/site/listing';
import { ArticleHeroGallery } from '@/components/site/article-hero-gallery';
import { ArticleStickyTitle } from '@/components/site/article-sticky-title';
import { ArticleBodyView } from '@/components/site/article-body';
import { ArticleShare } from '@/components/site/article-share';
import { ArticleStaffStats } from '@/components/site/article-staff-stats';
import { articleShareUrl } from '@/lib/share-links';
import { getAshkelonShabbatTimes } from '@/lib/shabbat';
import { CrosswordGame } from '@/components/site/crossword-game';
import { Cell, GridShell } from '@/components/site/shell';
import { HomeWeatherStrip, WeatherPageView as WeatherPageBody } from '@/components/site/weather';
import { CinemaPageView as CinemaPageBody, HomeCinemaStrip } from '@/components/site/cinema';
import type { Ad, Article, Author, HomepageLayoutMode, SeaForecastPeriod, Weather, CinemaScreening } from '@/lib/content-types';
import {
    ARTICLE_INLINE_SLOTS,
    ARTICLE_SIDEBAR_SLOTS,
    CATEGORY_INLINE_SLOTS,
    HOMEPAGE_LEADERBOARD_SLOTS,
} from '@/lib/ads';
import { isLiveReportActive } from '@/lib/article-card';
import { articleBodyPlainText } from '@/lib/article-body';
import { SITE_SOCIAL_LINKS } from '@/lib/site-social';
import { previewSrcSet } from '@/lib/media-url';
import { formatHebrewDate, getReadingTime } from '@/lib/timeUtils';
import type { CardArticle } from '@/components/site/listing';
import type { HomePageDeferredData } from '@/server/functions/pages';

/** Rotate layouts so consecutive homepage rails don't look identical. */
const HOME_RAIL_VARIANT: Record<string, CategoryRailVariant> = {
    featured: 'dual',
    news: 'mosaic',
    sports: 'spotlight',
    crime: 'docket',
    transport: 'board',
    business: 'thumbs',
    education: 'grid',
    entertainment: 'feature',
    more: 'thumbs',
};

const EMPTY_HOME_DEFERRED: HomePageDeferredData = {
    cinemaScreenings: [],
    alerts: [],
    talkingAboutUs: [],
    ads: [],
    leaderboardAds: [],
    dailyPhoto: null,
    crossword: null,
    weeklyTop: [],
    weather: null,
    sea: [],
};

function homeShabbatTimes() {
    try {
        return getAshkelonShabbatTimes();
    } catch {
        return null;
    }
}

function HomeDeferred({
    deferred,
    fallback,
    children,
}: {
    deferred: Promise<HomePageDeferredData>;
    fallback?: React.ReactNode;
    children: (data: HomePageDeferredData) => React.ReactNode;
}) {
    return (
        <Await promise={deferred} fallback={fallback ?? null}>
            {children}
        </Await>
    );
}

type HomeCategory = { cat?: { name: string; key: string }; articles: CardArticle[] };

const EMPTY_HOME_CATEGORY: HomeCategory = { articles: [] };

function ensureArray<T>(value: T[] | null | undefined): T[] {
    return Array.isArray(value) ? value : [];
}

function homeCategory(value: HomeCategory | null | undefined): HomeCategory {
    if (!value) return EMPTY_HOME_CATEGORY;
    return {
        cat: value.cat,
        articles: ensureArray(value.articles),
    };
}

export function HomePageView({
    featured,
    news,
    crime,
    sports,
    transport,
    business,
    education,
    entertainment,
    more,
    layoutMode = 'columns',
    deferred,
}: {
    featured?: CardArticle[];
    news?: HomeCategory;
    crime?: HomeCategory;
    sports?: HomeCategory;
    transport?: HomeCategory;
    business?: HomeCategory;
    education?: HomeCategory;
    entertainment?: HomeCategory;
    more?: { title: string; articles: CardArticle[] };
    layoutMode?: HomepageLayoutMode;
    deferred: Promise<HomePageDeferredData>;
}) {
    const { canAccessAdmin = false } = useLoaderData({ from: '__root__' });
    const featuredArticles = ensureArray(featured);
    const newsSection = homeCategory(news);
    const crimeSection = homeCategory(crime);
    const sportsSection = homeCategory(sports);
    const transportSection = homeCategory(transport);
    const businessSection = homeCategory(business);
    const educationSection = homeCategory(education);
    const entertainmentSection = homeCategory(entertainment);
    const moreSection = {
        title: more?.title ?? 'עוד באשקלון',
        articles: ensureArray(more?.articles),
    };

    const [hero, ...restFeatured] = featuredArticles;
    const shabbatTimes = homeShabbatTimes();
    const shabbatBox = (
        <div className="px-6 pb-6">
            <HomeShabbatStrip times={shabbatTimes} />
        </div>
    );

    const sections: { key: string; node: React.ReactNode }[] = [];

    if (restFeatured.length > 0) {
        sections.push({
            key: 'featured',
            node: (
                <CategoryRail
                    title="עוד בכותרות"
                    articles={restFeatured}
                    variant={HOME_RAIL_VARIANT.featured}
                />
            ),
        });
    }

    if (newsSection.articles.length > 0) {
        sections.push({
            key: 'news',
            node: (
                <CategoryRail
                    title={newsSection.cat?.name ?? 'חדשות'}
                    articles={newsSection.articles}
                    categoryKey={newsSection.cat?.key ?? 'news'}
                    variant={HOME_RAIL_VARIANT.news}
                />
            ),
        });
    }

    if (sportsSection.articles.length > 0) {
        sections.push({
            key: 'sports',
            node: (
                <CategoryRail
                    title={sportsSection.cat?.name ?? 'ספורט'}
                    articles={sportsSection.articles}
                    categoryKey={sportsSection.cat?.key ?? 'sports'}
                    variant={HOME_RAIL_VARIANT.sports}
                />
            ),
        });
    }

    if (crimeSection.articles.length > 0) {
        sections.push({
            key: 'crime',
            node: (
                <CategoryRail
                    title={crimeSection.cat?.name ?? 'פלילי'}
                    articles={crimeSection.articles}
                    categoryKey={crimeSection.cat?.key ?? 'crime'}
                    variant={HOME_RAIL_VARIANT.crime}
                />
            ),
        });
    }

    if (transportSection.articles.length > 0) {
        sections.push({
            key: 'transport',
            node: (
                <CategoryRail
                    title={transportSection.cat?.name ?? 'תחבורה'}
                    articles={transportSection.articles}
                    categoryKey={transportSection.cat?.key ?? 'transport'}
                    variant={HOME_RAIL_VARIANT.transport}
                />
            ),
        });
    }

    if (businessSection.articles.length > 0) {
        sections.push({
            key: 'business',
            node: (
                <CategoryRail
                    title={businessSection.cat?.name ?? 'עסקים'}
                    articles={businessSection.articles}
                    categoryKey={businessSection.cat?.key ?? 'business'}
                    variant={HOME_RAIL_VARIANT.business}
                />
            ),
        });
    }

    if (educationSection.articles.length > 0) {
        sections.push({
            key: 'education',
            node: (
                <CategoryRail
                    title={educationSection.cat?.name ?? 'חינוך'}
                    articles={educationSection.articles}
                    categoryKey={educationSection.cat?.key ?? 'education'}
                    variant={HOME_RAIL_VARIANT.education}
                />
            ),
        });
    }

    if (entertainmentSection.articles.length > 0) {
        const entertainmentTitle = entertainmentSection.cat?.name ?? 'בידור';
        const entertainmentKey = entertainmentSection.cat?.key ?? 'entertainment';

        sections.push({
            key: 'entertainment',
            node: (
                <>
                    <CategoryRail
                        title={entertainmentTitle}
                        articles={entertainmentSection.articles}
                        categoryKey={entertainmentKey}
                        variant={HOME_RAIL_VARIANT.entertainment}
                    />
                    <HomeDeferred deferred={deferred}>
                        {(data) =>
                            data.cinemaScreenings.length > 0 ? (
                                <HomeCinemaStrip
                                    screenings={data.cinemaScreenings}
                                    className="border-t border-white/10"
                                />
                            ) : null
                        }
                    </HomeDeferred>
                </>
            ),
        });
    }

    const cinemaOnlySection =
        entertainmentSection.articles.length === 0 ? (
            <HomeDeferred deferred={deferred}>
                {(data) => {
                    if (data.cinemaScreenings.length === 0) return null;
                    const entertainmentTitle = entertainmentSection.cat?.name ?? 'בידור';
                    const entertainmentKey = entertainmentSection.cat?.key ?? 'entertainment';
                    return (
                        <div className="border-t border-white/10">
                            <div className="flex items-end justify-between gap-4 border-b border-white/10 px-6 py-3">
                                <h2 className="title-single-line text-base font-normal tracking-tight text-white md:text-lg">
                                    {entertainmentTitle}
                                </h2>
                                {entertainmentSection.cat?.key ? (
                                    <Link
                                        to="/category/$key"
                                        params={{ key: entertainmentKey }}
                                        className="shrink-0 text-sm text-white/55 hover:text-white"
                                    >
                                        למדור ←
                                    </Link>
                                ) : null}
                            </div>
                            <HomeCinemaStrip screenings={data.cinemaScreenings} />
                        </div>
                    );
                }}
            </HomeDeferred>
        ) : null;

    if (moreSection.articles.length > 0) {
        sections.push({
            key: 'more',
            node: (
                <CategoryRail
                    title={moreSection.title}
                    articles={moreSection.articles}
                    variant={HOME_RAIL_VARIANT.more}
                />
            ),
        });
    }

    const heroFull = layoutMode === 'hero-full';
    const topMeta = Boolean(hero);
    const feedStartsWithTopBorder = !heroFull && Boolean(hero);

    const weatherStrip = (
        <HomeDeferred deferred={deferred}>
            {(data) => <HomeWeatherStrip weather={data.weather} sea={data.sea} />}
        </HomeDeferred>
    );

    const alertsStrip = (
        <HomeDeferred deferred={deferred}>
            {(data) =>
                data.alerts.length > 0 ? (
                    <HomeAlertsStrip
                        alerts={data.alerts}
                        className={hero || heroFull ? 'border-t border-white/10' : undefined}
                    />
                ) : null
            }
        </HomeDeferred>
    );

    const columnsGridClass = 'lg:grid lg:grid-cols-[minmax(0,1fr)_300px]';

    const mainFeed = sections.map((section, index) => (
        <div
            key={section.key}
            className={
                index > 0 || feedStartsWithTopBorder
                    ? 'border-t border-white/10'
                    : undefined
            }
        >
            {section.node}
        </div>
    ));

    // Hero-full already draws border-t on the feed+sidebar wrapper; a second
    // border-t on the leaderboard stacks into a double line under מבזקים.
    const leaderboardNeedsTopBorder = !(heroFull && topMeta);
    const leaderboard = (
        <HomeDeferred deferred={deferred}>
            {(data) =>
                data.leaderboardAds.length > 0 ? (
                    <AdPlacementRail
                        slots={HOMEPAGE_LEADERBOARD_SLOTS}
                        ads={data.leaderboardAds}
                        className={
                            leaderboardNeedsTopBorder
                                ? 'hidden border-t border-white/10 px-6 py-6 md:block'
                                : 'hidden px-6 py-6 md:block'
                        }
                    />
                ) : null
            }
        </HomeDeferred>
    );

    const sidebar = (compact: boolean) => (
        <HomeDeferred
            deferred={deferred}
            fallback={
                <HomeSidebar
                    talkingAboutUs={EMPTY_HOME_DEFERRED.talkingAboutUs}
                    ads={EMPTY_HOME_DEFERRED.ads}
                    compact={compact}
                />
            }
        >
            {(data) => (
                <HomeSidebar
                    talkingAboutUs={data.talkingAboutUs}
                    ads={data.ads}
                    compact={compact}
                />
            )}
        </HomeDeferred>
    );

    return (
        <GridShell activePath="/">
            {heroFull ? (
                /*
                  Hero-full: featured story spans the full Cell width.
                  Feed + sidebar sit in a two-column grid below the hero.
                */
                <Cell>
                    {weatherStrip}
                    {!hero && (
                        <>
                            <div className="px-6 pt-6">
                                <h1 className="text-sm font-normal text-white/55">חדשות אשקלון</h1>
                            </div>
                            {shabbatBox}
                        </>
                    )}
                    {hero && (
                        <div>
                            <FeaturedHero article={hero} fullWidth />
                            {shabbatBox}
                        </div>
                    )}
                    {alertsStrip}
                    <div
                        className={
                            topMeta
                                ? 'border-t border-white/10 lg:grid lg:grid-cols-[minmax(0,1fr)_300px]'
                                : 'lg:grid lg:grid-cols-[minmax(0,1fr)_300px]'
                        }
                    >
                        {/*
                          Outer tracks stretch to the taller column; sticky on
                          each inner wrapper pins the shorter side while scrolling.
                        */}
                        <div className="min-w-0 lg:border-e lg:border-white/10">
                            <div className="lg:sticky lg:top-16">
                                {leaderboard}
                                {mainFeed}
                                {cinemaOnlySection}
                            </div>
                        </div>
                        <div className="hidden lg:block">
                            <div className="sticky top-16">
                                {sidebar(false)}
                            </div>
                        </div>
                    </div>
                </Cell>
            ) : (
                /*
                  Columns (RTL): main feed is visual-right, sidebar visual-left.
                  Outer tracks stretch to the taller column; sticky on each inner
                  wrapper pins whichever side is shorter while the other scrolls.
                */
                <Cell>
                    {weatherStrip}
                    <div className={columnsGridClass}>
                        <div className="min-w-0 lg:border-e lg:border-white/10">
                            <div className="lg:sticky lg:top-16">
                                {!hero && (
                                    <div className="px-6 pt-6">
                                        <h1 className="text-sm font-normal text-white/55">
                                            חדשות אשקלון
                                        </h1>
                                    </div>
                                )}
                                {!hero ? shabbatBox : null}
                                {hero && <FeaturedHero article={hero} />}
                                {hero ? shabbatBox : null}
                                {alertsStrip}
                                {leaderboard}
                                {mainFeed}
                                {cinemaOnlySection}
                            </div>
                        </div>

                        <div className="hidden lg:block">
                            <div className="sticky top-16">
                                {sidebar(false)}
                            </div>
                        </div>
                    </div>
                </Cell>
            )}

            <HomeDeferred deferred={deferred}>
                {(data) => (
                    <>
                        {data.dailyPhoto ? <PictureMoment article={data.dailyPhoto} /> : null}
                        {data.crossword ? (
                            <CrosswordGame puzzle={data.crossword} canBrowseDays={canAccessAdmin} />
                        ) : null}
                        {data.weeklyTop.length > 0 ? (
                            <WeeklyTopRanking
                                items={data.weeklyTop}
                                connected={Boolean(data.dailyPhoto)}
                                className={
                                    data.crossword && !data.dailyPhoto
                                        ? 'lg:border-t-0'
                                        : undefined
                                }
                            />
                        ) : null}
                    </>
                )}
            </HomeDeferred>

            {/* Mobile: stack compact rail under the feed (ads trimmed). */}
            <Cell className="lg:hidden">
                {sidebar(true)}
            </Cell>
        </GridShell>
    );
}

function AuthorSignature({ authors }: { authors: Author[] }) {
    if (authors.length === 0) return null;

    const multi = authors.length > 1;

    return (
        <div>
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
                <p className="text-sm text-white/55">נכתב על ידי</p>
                {!multi && (
                    <Link
                        to="/author/$id"
                        params={{ id: authors[0]!.$id }}
                        className="text-sm text-white/55 transition-colors hover:text-white"
                    >
                        לפרופיל
                    </Link>
                )}
            </div>
            <ul className={multi ? 'divide-y divide-white/10' : undefined}>
                {authors.map((author) => (
                    <li key={author.$id}>
                        <Link
                            to="/author/$id"
                            params={{ id: author.$id }}
                            className="group flex gap-5 p-6 sm:items-start"
                        >
                            {author.profileUrl && (
                                <img
                                    src={author.profileUrl}
                                    srcSet={previewSrcSet(author.profileUrl)}
                                    sizes="(min-width: 640px) 64px, 56px"
                                    alt={author.name}
                                    width={64}
                                    height={64}
                                    className="size-14 shrink-0 rounded-full border border-white/10 object-cover grayscale sm:size-16"
                                />
                            )}
                            <div className="min-w-0">
                                <p className="text-lg font-semibold tracking-tight text-white transition-colors group-hover:text-white/85 sm:text-xl">
                                    {author.name}
                                </p>
                                {author.title && (
                                    <p className="mt-1 text-sm text-white/55">{author.title}</p>
                                )}
                                {author.biography && (
                                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55">
                                        {author.biography}
                                    </p>
                                )}
                                {multi && (
                                    <p className="mt-2 text-sm text-white/55 transition-colors group-hover:text-white/70">
                                        לפרופיל
                                    </p>
                                )}
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function ArticlePageView({
    article,
    related,
    latest = [],
    inlineAds = [],
    sidebarAds = [],
}: {
    article: Article;
    related: CardArticle[];
    latest?: CardArticle[];
    inlineAds?: Ad[];
    sidebarAds?: Ad[];
}) {
    const { canAccessAdmin = false } = useLoaderData({ from: '__root__' });
    const plainBody = articleBodyPlainText(article.body);
    const showLiveBadge = isLiveReportActive(article.live, article.date);
    const readingTime = getReadingTime(plainBody);
    const wasUpdatedLater =
        new Date(article.$updatedAt).getTime() - new Date(article.date).getTime() > 60_000;
    const primaryCategory = article.categories[0];
    const hasSidebarAds = sidebarAds.length > 0;
    const [titlePinned, setTitlePinned] = useState(false);

    return (
        <GridShell>
            <Cell crosses={false}>
                <div className="relative">
                    <div className="grid-dots" aria-hidden />
                    <div className="relative px-6 pt-6 pb-10 md:pt-8 md:pb-12">
                        <div className="flex items-start justify-between gap-4">
                            <nav aria-label="breadcrumb" className="text-sm text-white/55">
                                <Link to="/" className="hover:text-white">ראשי</Link>
                                {primaryCategory && (
                                    <>
                                        <span className="mx-2 text-white/20">/</span>
                                        <Link
                                            to="/category/$key"
                                            params={{ key: primaryCategory.key }}
                                            className="hover:text-white"
                                        >
                                            {primaryCategory.name}
                                        </Link>
                                    </>
                                )}
                            </nav>
                            {canAccessAdmin ? (
                                <Link
                                    to="/admin/articles/$id"
                                    params={{ id: article.$id }}
                                    className="inline-flex shrink-0 items-center gap-1.5 border border-white/15 px-2.5 py-1 text-sm text-white/55 transition-colors hover:border-white/30 hover:text-white"
                                >
                                    <Pencil className="size-3.5" aria-hidden />
                                    עריכה
                                </Link>
                            ) : null}
                        </div>

                        {article.trailer && (
                            <p className="mt-4 text-sm tracking-wide text-brand-accent">
                                {article.trailer}
                            </p>
                        )}

                        <ArticleStickyTitle
                            title={article.title}
                            shareUrl={articleShareUrl(article)}
                            onPinnedChange={setTitlePinned}
                            className={
                                article.trailer
                                    ? 'mt-2 max-w-3xl text-3xl font-normal tracking-[-0.04em] md:text-5xl'
                                    : 'mt-5 max-w-3xl text-3xl font-normal tracking-[-0.04em] md:text-5xl'
                            }
                        />

                        {article.subtitle && (
                            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/55 md:text-lg">
                                {article.subtitle}
                            </p>
                        )}

                        {article.heroImages.length > 0 && (
                            <div className="relative -mx-6 mt-8">
                                <ArticleHeroGallery
                                    images={article.heroImages}
                                    title={article.title}
                                />
                            </div>
                        )}

                        <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-white/55">
                            {showLiveBadge && (
                                <span className="border border-brand-accent px-2 py-0.5 text-brand-accent">
                                    שידור חי
                                </span>
                            )}
                            {article.authors.length > 0 && (
                                <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                    {article.authors.map((author, i) => (
                                        <Fragment key={author.$id}>
                                            {i > 0 && (
                                                <span className="text-white/25" aria-hidden>
                                                    ·
                                                </span>
                                            )}
                                            <Link
                                                to="/author/$id"
                                                params={{ id: author.$id }}
                                                className="text-white/70 hover:text-white"
                                            >
                                                {author.name}
                                            </Link>
                                        </Fragment>
                                    ))}
                                </span>
                            )}
                            <time dateTime={article.date}>
                                {formatHebrewDate(article.date)}
                            </time>
                            {wasUpdatedLater && (
                                <time dateTime={article.$updatedAt}>
                                    עודכן {formatHebrewDate(article.$updatedAt)}
                                </time>
                            )}
                            <span>{readingTime.label}</span>
                        </div>

                        {article.categoryKeys.length > 0 && (
                            <ul className="mt-5 flex flex-wrap gap-2">
                                {article.categories.map((cat) => (
                                    <li key={cat.key}>
                                        <Link
                                            to="/category/$key"
                                            params={{ key: cat.key }}
                                            className="block text-sm text-white/55 border border-white/10 px-2.5 py-1 transition-colors hover:border-white/25 hover:text-white"
                                        >
                                            {cat.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </Cell>

            {canAccessAdmin ? (
                <Cell>
                    <ArticleStaffStats articleId={article.$id} />
                </Cell>
            ) : null}

            <Cell>
                <div
                    className={
                        hasSidebarAds
                            ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_300px]'
                            : undefined
                    }
                >
                    <div className={hasSidebarAds ? 'min-w-0 lg:border-e lg:border-white/10' : undefined}>
                        <div className="px-6 py-12">
                            <ArticleShare article={article} className="mb-6" />
                            <article
                                className="prose-grid"
                                itemScope
                                itemType="https://schema.org/NewsArticle"
                            >
                                {/* space-y here: body <p>s are nested, not direct .prose-grid children */}
                                <div itemProp="articleBody" className="space-y-6">
                                    <ArticleBodyView
                                        sections={article.body}
                                        imagesById={article.imagesById}
                                    />
                                </div>
                            </article>
                            <ArticleShare article={article} className="mt-6" />
                            <AdPlacementRail
                                slots={ARTICLE_INLINE_SLOTS}
                                ads={inlineAds}
                                className="mt-10"
                            />
                            <aside
                                data-nosnippet
                                className="mx-auto mt-10 max-w-2xl border-t border-white/10 pt-6 text-xs leading-relaxed text-white/55"
                            >
                                <p>
                                    תיעוד מרשתות חברתיות, שימוש לפי סעיף 27 א׳ לחוק זכויות יוצרים
                                </p>
                                <p className="mt-2">
                                    אנו מכבדים זכויות יוצרים ועושים מאמץ לאתר את בעלי הזכויות
                                    בצילומים המגיעים לידינו. אם זיהיתם בפרסומינו צילום שיש לכם
                                    זכויות בו, אתם רשאים לפנות אלינו ולבקש לחדול מהשימוש
                                </p>
                            </aside>
                        </div>
                    </div>
                    {hasSidebarAds ? (
                        <div className="hidden lg:block">
                            <div className={titlePinned ? 'sticky top-28' : 'sticky top-16'}>
                                <AdPlacementRail
                                    slots={ARTICLE_SIDEBAR_SLOTS}
                                    ads={sidebarAds}
                                    className="space-y-0"
                                    itemClassName="border-b border-white/10 px-6 py-6"
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </Cell>

            {article.authors.length > 0 && (
                <Cell>
                    <AuthorSignature authors={article.authors} />
                </Cell>
            )}

            {related.length > 0 && (
                <Cell>
                    <CategoryRail
                        title="עוד כתבות"
                        articles={related}
                        variant="dual"
                    />
                </Cell>
            )}

            {latest.length > 0 && (
                <Cell>
                    <CategoryRail
                        title="עכשיו באתר"
                        articles={latest}
                        variant="mosaic"
                    />
                </Cell>
            )}
        </GridShell>
    );
}

export function CategoryPageView({
    category,
    articles,
    ads = [],
}: {
    category: { name: string; key: string; description?: string; pictureUrl?: string };
    articles: CardArticle[];
    ads?: Ad[];
}) {
    const mid = Math.min(6, Math.max(3, Math.floor(articles.length / 2)));
    const before = articles.slice(0, mid);
    const after = articles.slice(mid);

    return (
        <GridShell activePath={`/category/${category.key}`}>
            <Cell crosses={false}>
                <div className="relative">
                    <div className="grid-dots" aria-hidden />
                    <div className="relative px-6 py-16 md:py-20">
                        <p className="text-sm text-white/55">מדור</p>
                        <h1 className="mt-4 max-w-3xl text-4xl font-normal tracking-[-0.04em] md:text-5xl">
                            {category.name}
                        </h1>
                        {category.description && (
                            <p className="mt-5 max-w-xl text-lg text-white/55">{category.description}</p>
                        )}
                    </div>
                </div>
            </Cell>

            {category.pictureUrl && (
                <Cell crosses={false}>
                    <img
                        src={category.pictureUrl}
                        alt={category.name}
                        className="aspect-[21/9] w-full border-y border-white/10 object-cover"
                    />
                </Cell>
            )}

            <Cell>
                <SectionArticles title="כתבות" articles={before.length ? before : articles} />
                {before.length > 0 && after.length > 0 ? (
                    <AdPlacementRail
                        slots={CATEGORY_INLINE_SLOTS}
                        ads={ads}
                        className="border-t border-white/10 px-6 py-8"
                    />
                ) : null}
                {after.length > 0 ? (
                    <div className="border-t border-white/10">
                        {after.map((article, index) => (
                            <ArticleRow
                                key={article.slug}
                                article={article}
                                showBorder={index < after.length - 1}
                            />
                        ))}
                    </div>
                ) : null}
            </Cell>
        </GridShell>
    );
}

export function AuthorPageView({
    author,
    articles,
}: {
    author: Author;
    articles: CardArticle[];
}) {
    return (
        <GridShell>
            <Cell crosses={false}>
                <div className="relative px-6 py-16 md:py-20">
                    <p className="text-sm text-white/55">כותב/ת</p>
                    <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-start">
                        {author.profileUrl && (
                            <img
                                src={author.profileUrl}
                                srcSet={previewSrcSet(author.profileUrl)}
                                sizes="96px"
                                alt={author.name}
                                className="size-24 rounded-full border border-white/10 object-cover grayscale"
                                width={96}
                                height={96}
                            />
                        )}
                        <div>
                            <h1 className="text-3xl font-normal tracking-[-0.03em] md:text-4xl">
                                {author.name}
                            </h1>
                            {author.title && (
                                <p className="text-sm text-white/55 mt-2">{author.title}</p>
                            )}
                            {author.biography && (
                                <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/55">
                                    {author.biography}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </Cell>

            <Cell>
                <SectionArticles title="כתבות" articles={articles} />
            </Cell>
        </GridShell>
    );
}

export function AboutPageView() {
    const telegram = SITE_SOCIAL_LINKS.find((link) => link.id === 'telegram');

    return (
        <GridShell activePath="/about">
            <Cell>
                <div className="relative px-6 py-16 md:py-24">
                    <div className="grid-dots" aria-hidden />
                    <div className="relative">
                        <p className="text-sm text-white/55">אודות</p>
                        <h1 className="mt-4 max-w-3xl text-4xl font-normal tracking-[-0.04em] md:text-5xl">
                            אשקלון ניוז - חדשות אשקלון
                        </h1>
                        <div className="mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            <p>
                                אשקלון ניוז הוא מקור החדשות המוביל בעיר אשקלון מאז 2006. אנחנו מביאים
                                לכם חדשות אשקלון מהשטח: מבזקים, עדכונים שוטפים וכתבות מהעיר ומהסביבה.
                            </p>
                            <p>
                                הצוות שלנו עובד מסביב לשעון כדי לספק לכם מידע מדויק, עדכני ורלוונטי על
                                חדשות, כלכלה, חינוך, ספורט, תרבות ודיווחים מהשטח.
                            </p>
                            {telegram && (
                                <p>
                                    אפשר גם לעקוב אחרינו בטלגרם בכתובת{' '}
                                    <a
                                        href={telegram.href}
                                        className="text-white hover:text-white/80"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {telegram.href.replace(/^https?:\/\//, '')}
                                    </a>
                                    .
                                </p>
                            )}
                            <p>
                                ראיתם משהו ששווה לדווח?{' '}
                                <Link to="/tip" className="text-white hover:text-white/80">
                                    שלחו לנו ידיעה
                                </Link>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            </Cell>
        </GridShell>
    );
}

export function AccessibilityPageView() {
    return (
        <GridShell activePath="/accessibility">
            <Cell>
                <div className="relative px-6 py-16 md:py-24">
                    <div className="grid-dots" aria-hidden />
                    <article className="relative max-w-2xl">
                        <p className="text-sm text-white/55">נגישות</p>
                        <h1 className="mt-4 text-4xl font-normal tracking-[-0.04em] md:text-5xl">
                            הצהרת נגישות
                        </h1>

                        <div className="mt-8 space-y-5 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            <p>
                                אשקלון ניוז מאמין שלכל אדם מגיעה גישה שווה למידע. אנחנו פועלים כדי
                                שהאתר יהיה נוח לשימוש, כולל לאנשים עם מוגבלות.
                            </p>
                            <p>
                                אנו שואפים לפעול בהתאם להמלצות התקן הישראלי (ת"י 5568) ולמסמך WCAG
                                הבינלאומי, ככל האפשר. נמשיך לשפר את נגישות האתר לאורך זמן.
                            </p>
                            <p>
                                בהתאם להוראות הדין, מפעיל האתר עומד בתנאים לקבלת פטור מביצוע התאמות
                                נגישות בשירותי אינטרנט בשל מחזור ההכנסות השנתי, הנמוך מהרף הקבוע
                                בתקנות. על אף קיומו של הפטור, אנו ממשיכים לפעול, ככל הניתן, לשיפור
                                נגישות האתר עבור כלל המשתמשים.
                            </p>
                        </div>

                        <h2 className="mt-12 text-xl font-normal text-white md:text-2xl">
                            התאמות באתר
                        </h2>
                        <ul className="mt-4 list-disc space-y-2 ps-5 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            <li>שפה עברית וכיוון כתיבה מימין לשמאל</li>
                            <li>מבנה עמודים עם כותרות וניווט ברור</li>
                            <li>קישור לדלג לתוכן הראשי</li>
                            <li>תמיכה בניווט מקלדת</li>
                            <li>טקסט חלופי לתמונות</li>
                            <li>תמיכה בהגדלת תצוגה בדפדפן</li>
                        </ul>

                        <h2 className="mt-12 text-xl font-normal text-white md:text-2xl">
                            שירותי צד שלישי
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            חלקים באתר מסתמכים על שירותים חיצוניים, כמו רשתות חברתיות או פרסום.
                            אלה לא תמיד בשליטתנו, וייתכן שיציבו קשיים שאין לנו דרך לתקן.
                        </p>

                        <h2 className="mt-12 text-xl font-normal text-white md:text-2xl">
                            פניות
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            נתקלתם בקושי בנושא נגישות? נשמח לקבל פנייה ולטפל בה.
                        </p>
                        <p className="mt-3 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            <ObfuscatedContactEmail />
                        </p>

                        <p className="mt-10 text-base leading-relaxed text-white/55 md:text-lg md:leading-8">
                            חרף המאמצים, ייתכן שעדיין יימצאו דפים או רכיבים שדורשים שיפור. אנחנו
                            ממשיכים לעבוד על כך.
                        </p>

                        <p className="mt-12 text-sm text-white/55">
                            תאריך עדכון: 30.08.2026
                        </p>
                    </article>
                </div>
            </Cell>
        </GridShell>
    );
}

export function WeatherPageView({
    primary,
    cities,
    sea = [],
}: {
    primary: Weather | null;
    cities: Weather[];
    sea?: SeaForecastPeriod[];
}) {
    return (
        <GridShell activePath="/weather">
            <Cell>
                <WeatherPageBody primary={primary} cities={cities} sea={sea} />
            </Cell>
        </GridShell>
    );
}

export function CinemaPageView({ screenings }: { screenings: CinemaScreening[] }) {
    return (
        <GridShell activePath="/cinema">
            <Cell>
                <CinemaPageBody screenings={screenings} />
            </Cell>
        </GridShell>
    );
}
